import React from 'react';

import PropTypes from 'prop-types';

import { useDevice } from '@bonnet/next/device';

import { Heading, Image, Link, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxPrivateSellerExchangeLogoClick } from 'reaxl-analytics-handlers';
import { useFeatures } from 'reaxl-features';

import FilterLocationModalContainer from './FilterLocationModalContainer';
import SRPBreadcrumbsContainer from './SRPBreadcrumbsContainer';

function SrpHeroContainer({
    backToResultsPath,
    locationHeading,
    savedSearchTitle,
    sellerType,
}) {
    const {
        // feature configs
        enable_private_seller_exchange_logo: [enablePrivateSellerExchangeLogo],
        srp_hero: [, {
            secondary_link: secondaryLink,
            secondary_link_text: secondaryLinkText,
            link_text: linkText,
            sub_text: subText,
        }],
        personalization: [isPersonalizationEnabled] = [],
        psx_logo: [, {
            icon: privateSellerIcon,
            href: privateSellerExchangeLogoHref,
            width: privateSellerIconWidth,
        }],
    } = useFeatures([
        'enable_private_seller_exchange_logo',
        'srp_hero',
        'personalization',
        'psx_logo',
    ]);

    const device = useDevice();
    const isXs = device?.is?.xs ?? false;

    const handlePrivateSellerExchangeLogoClick = (e) => {
        sendClick(psxPrivateSellerExchangeLogoClick, e);
    };

    const onlyPrivateSellerSelected = !Array.isArray(sellerType) && sellerType === 'p';

    return (
        <>
            {isPersonalizationEnabled && savedSearchTitle && (
                <Text
                    color="text-subdued"
                    data-cmp="saved-search-title"
                >
                    {savedSearchTitle}
                </Text>
            )}
            {enablePrivateSellerExchangeLogo && onlyPrivateSellerSelected && (
                <Link
                    target="_blank"
                    href={privateSellerExchangeLogoHref}
                    onClick={handlePrivateSellerExchangeLogoClick}
                >
                    <Image
                        src={privateSellerIcon}
                        alt="Private Seller Exchange"
                        height={27}
                        width={privateSellerIconWidth}
                        lazyload={false}
                        loading="eager"
                    />
                </Link>
            )}

            <div className="margin-bottom-2"><SRPBreadcrumbsContainer /></div>

            {linkText && (
                <div className="margin-bottom-2">
                    <Link
                        href={backToResultsPath ?? 'cars-for-sale/all-cars'}
                        className="text-bold"
                        undecorated
                    >
                        {linkText}
                    </Link>
                </div>
            )}

            <div style={{ minHeight: isXs ? 'calc((var(--sem-type-heading-4) * 1.2) * 2)' : '' }}>
                <Heading
                    componentClass="h1"
                    className="display-inline margin-right-2"
                    data-cmp="srp-title"
                    level={4}
                    id="search-results-heading"
                >
                    {locationHeading}
                </Heading>

                <FilterLocationModalContainer className="text-link link-undecorated" />
            </div>

            <div className="display-md-flex justify-content-md-between align-items-end flex-wrap">
                {subText && (
                    <Text
                        componentClass="div"
                        color="accent"
                        weight="bold"
                    >
                        {subText}
                    </Text>
                )}
                {secondaryLink && (
                    <Link
                        target="_blank"
                        className="text-bold text-size-200"
                        href={secondaryLink}
                        undecorated
                    >
                        {secondaryLinkText}
                    </Link>
                )}
            </div>
        </>
    );
}

SrpHeroContainer.propTypes = {
    /**
     * Optional string for the back-to-results link
     */
    backToResultsPath: PropTypes.string,
    /**
     * Required string for the location heading
     */
    locationHeading: PropTypes.string.isRequired,
    /**
     * Optional string for the saved search title
     */
    savedSearchTitle: PropTypes.string,
    /**
     * String or an array of the seller types
     */
    sellerType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default SrpHeroContainer;
